/**
 * Derives the state of the manually-scheduled maintenance banner from the
 * `dashboard-scheduled-maintenance-banner` Unleash variant.
 *
 * ThoughtSpot exposes no reliable API for *upcoming* per-cluster maintenance —
 * only the live `/system/banner` status (handled separately in RootLayout). It
 * does notify us of planned outages by email ahead of time, so this gives our
 * team a self-service advance-warning path: set the variant's JSON payload
 * (`message` + `endsAt`, optional `startsAt`) and enable the flag. The banner
 * shows while the flag is enabled and the current time is within the window,
 * then auto-hides once `endsAt` passes. Omit `startsAt` to show as soon as the
 * flag is enabled; set it to pre-stage the flag and have the banner appear
 * later (e.g. enable now, surface 48h before the outage).
 *
 * Fails safe: any missing/malformed variant, payload, or timestamp yields an
 * inactive state rather than throwing.
 */

import { useVariant } from '@alchemer/unleash';
import { FEATURE_FLAGS } from '@/config/features';
import { useBoundStore } from '@/store';
import type { ScheduledMaintenancePayload } from '@/types/thoughtspot';

type ScheduledBannerState = { isActive: false } | { isActive: true; message: string };
type UnleashVariant = ReturnType<typeof useVariant>;

/** Whether `value` is a string that parses to a valid date. */
function isValidIso(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(new Date(value).getTime());
}

/**
 * Narrows an unknown parsed payload to a valid {@link ScheduledMaintenancePayload}.
 * Requires a non-empty `message`, a parseable ISO `endsAt`, and — if present — a
 * parseable ISO `startsAt`.
 */
function isValidPayload(value: unknown): value is ScheduledMaintenancePayload {
  if (typeof value !== 'object' || value === null) return false;
  const { message, endsAt, startsAt } = value as Record<string, unknown>;
  return (
    typeof message === 'string' &&
    message.trim().length > 0 &&
    isValidIso(endsAt) &&
    (startsAt === undefined || isValidIso(startsAt))
  );
}

/**
 * Extracts the raw (still-unknown) JSON payload from an Unleash variant. Unleash
 * JSON variants deliver the payload as a stringified `value`; it is not
 * auto-deserialized, so we parse it ourselves. Returns `null` for a disabled
 * variant, a non-JSON payload, or invalid JSON.
 */
function parseVariantPayload(variant: UnleashVariant): unknown {
  if (!variant?.enabled || variant.payload?.type !== 'json') return null;
  try {
    return JSON.parse(variant.payload.value);
  } catch {
    return null;
  }
}

export function useScheduledMaintenanceBanner(): ScheduledBannerState {
  const variant = useVariant(FEATURE_FLAGS.SCHEDULED_MAINTENANCE_BANNER);
  // Dev-only override from Debug Tools; takes precedence over the live variant.
  const debugPayload = useBoundStore((state) => state.debugScheduledMaintenanceBanner);

  const payload = debugPayload ?? parseVariantPayload(variant);
  if (!isValidPayload(payload)) {
    return { isActive: false };
  }

  const now = Date.now();
  const hasStarted = payload.startsAt === undefined || now >= new Date(payload.startsAt).getTime();
  const hasNotEnded = now < new Date(payload.endsAt).getTime();
  if (!hasStarted || !hasNotEnded) {
    return { isActive: false };
  }

  return { isActive: true, message: payload.message };
}
